import React, { useState, useEffect, useRef } from 'react';
import {
    X,
    AlertCircle,
    Clock,
    User,
    CheckCircle,
    Send,
    Loader2,
    Edit,
    Plus,
    Calendar,
    Monitor,
    ChevronDown,
    Download,
    Eye
} from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../contexts/AuthContext';

interface TicketMessage {
    id: string;
    ticket_id: string;
    sender_id: string;
    message: string;
    attachments: string[];
    is_internal: boolean;
    created_at: string;
    sender?: {
        name: string;
        role: string;
        unit_type: string | null;
    };
}

interface TicketDetailModalProps {
    isOpen: boolean;
    onClose: () => void;
    ticket: any;
    onUpdate: () => void;
}

const TicketDetailModal: React.FC<TicketDetailModalProps> = ({ isOpen, onClose, ticket, onUpdate }) => {
    const { user } = useAuth();
    const [messages, setMessages] = useState<TicketMessage[]>([]);
    const [newMessage, setNewMessage] = useState('');
    const [isInternal, setIsInternal] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isLoadingMessages, setIsLoadingMessages] = useState(false);
    const [showCannedResponses, setShowCannedResponses] = useState(false);
    const [staffList, setStaffList] = useState<any[]>([]);
    const [isUpdatingStatus, setIsUpdatingStatus] = useState(false);
    const messagesEndRef = useRef<HTMLDivElement>(null);

    const cannedResponses = [
        { title: 'Talebiniz Alındı', content: 'Merhaba, Destek talebiniz tarafımıza ulaşmıştır. Ekibimiz konuyla ilgili incelemeleri yapıp en kısa sürede size dönüş sağlayacaktır.' },
        { title: 'İşlem Tamamlandı', content: 'Merhaba, Bildirdiğiniz sorun çözümlenmiştir. Lütfen kontrol ederek bir sorun olması durumunda bize tekrar ulaşın.' },
        { title: 'Ek Bilgi Talebi', content: 'Merhaba, Konuyla ilgili daha detaylı inceleme yapabilmemiz için kullandığınız tarayıcı ve işlem yaptığınız sayfanın ekran görüntüsünü paylaşabilir misiniz?' },
        { title: 'Erişim Sorunu Çözümü', content: 'Merhaba, Yaşadığınız erişim sorunu giderilmiştir. Çerezlerinizi temizleyerek tekrar giriş yapmayı deneyebilirsiniz.' }
    ];

    useEffect(() => {
        if (isOpen && ticket) {
            fetchMessages();
            fetchStaff();
        }
    }, [isOpen, ticket]);

    useEffect(() => {
        scrollToBottom();
    }, [messages]);

    const scrollToBottom = () => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    };

    const fetchMessages = async () => {
        setIsLoadingMessages(true);
        try {
            const { data, error } = await supabase
                .from('ticket_messages')
                .select(`
                    *,
                    sender:profiles!ticket_messages_sender_id_fkey(name, role, unit_type)
                `)
                .eq('ticket_id', ticket.id)
                .order('created_at', { ascending: true });

            if (error) throw error;
            setMessages(data || []);
        } catch (error) {
            console.error('Error fetching messages:', error);
        } finally {
            setIsLoadingMessages(false);
        }
    };

    const fetchStaff = async () => {
        try {
            const { data, error } = await supabase
                .from('profiles')
                .select('id, name, role, unit_type')
                .eq('unit_type', 'teknik_destek')
                .eq('is_active', true);

            if (error) throw error;
            setStaffList(data || []);
        } catch (error) {
            console.error('Error fetching staff:', error);
        }
    };

    const handleSendMessage = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!user || !newMessage.trim()) return;

        setIsSubmitting(true);
        try {
            const { error } = await supabase
                .from('ticket_messages')
                .insert({
                    ticket_id: ticket.id,
                    sender_id: user.id,
                    message: newMessage,
                    is_internal: isInternal
                });

            if (error) throw error;
            setNewMessage('');
            setIsInternal(false);
            fetchMessages();
        } catch (error) {
            console.error('Error sending message:', error);
            alert('Mesaj gönderilemedi');
        } finally {
            setIsSubmitting(false);
        }
    };

    const updateStatus = async (status: string) => {
        setIsUpdatingStatus(true);
        try {
            const { error } = await supabase
                .from('support_tickets')
                .update({ status })
                .eq('id', ticket.id);

            if (error) throw error;
            onUpdate();
        } catch (error) {
            console.error('Error updating status:', error);
        } finally {
            setIsUpdatingStatus(false);
        }
    };

    const updateAssignment = async (assigned_to: string | null) => {
        try {
            const { error } = await supabase
                .from('support_tickets')
                .update({ assigned_to })
                .eq('id', ticket.id);

            if (error) throw error;
            onUpdate();
        } catch (error) {
            console.error('Error updating assignment:', error);
        }
    };

    if (!isOpen || !ticket) return null;

    const getPriorityColor = (priority: string) => {
        const colors: Record<string, string> = {
            dusuk: 'bg-gray-100 text-gray-600',
            orta: 'bg-blue-100 text-blue-600',
            yuksek: 'bg-orange-100 text-orange-600',
            acil: 'bg-red-100 text-red-600'
        };
        const labels: Record<string, string> = {
            dusuk: 'Düşük',
            orta: 'Orta',
            yuksek: 'Yüksek',
            acil: 'Acil'
        };
        return (
            <span className={`px-2 py-0.5 rounded text-xs font-semibold ${colors[priority]}`}>
                {labels[priority]}
            </span>
        );
    };

    return (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-[100] p-4 backdrop-blur-sm">
            <div className="bg-white dark:bg-gray-900 rounded-3xl shadow-2xl w-full max-w-6xl h-[90vh] flex flex-col overflow-hidden animate-in fade-in zoom-in duration-200">
                {/* Header */}
                <div className="px-6 py-4 border-b border-gray-100 dark:border-gray-800 flex items-center justify-between bg-white dark:bg-gray-900 z-10">
                    <div className="flex items-center gap-4">
                        <h2 className="text-xl font-bold text-gray-900 dark:text-white flex items-center gap-3">
                            {ticket.ticket_number}
                            <div className="flex gap-2">
                                <span className={`px-2 py-0.5 rounded text-xs font-semibold bg-blue-100 text-blue-600`}>
                                    {ticket.status.charAt(0).toUpperCase() + ticket.status.slice(1)}
                                </span>
                                {getPriorityColor(ticket.priority)}
                            </div>
                        </h2>
                    </div>
                    <div className="flex items-center gap-3">
                        <button className="flex items-center gap-2 px-3 py-1.5 text-sm font-medium text-purple-600 bg-purple-50 hover:bg-purple-100 rounded-lg transition-colors">
                            <Edit className="w-4 h-4" />
                            Düzenle
                        </button>
                        <button
                            onClick={onClose}
                            className="p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-full transition-colors text-gray-500"
                        >
                            <X className="w-6 h-6" />
                        </button>
                    </div>
                </div>

                <div className="flex-1 flex overflow-hidden">
                    {/* Left Side: Messages & Content */}
                    <div className="flex-1 flex flex-col bg-gray-50 dark:bg-gray-950 overflow-hidden">
                        <div className="flex-1 overflow-y-auto p-6 space-y-6">
                            {isLoadingMessages ? (
                                <div className="flex flex-col items-center justify-center h-full gap-2 text-gray-500">
                                    <Loader2 className="w-8 h-8 animate-spin text-purple-600" />
                                    <p className="text-sm">Mesajlar yükleniyor...</p>
                                </div>
                            ) : (
                                <>
                                    {/* Original Ticket Description */}
                                    <div className="bg-white dark:bg-gray-900 rounded-2xl p-6 shadow-sm border border-gray-100 dark:border-gray-800">
                                        <div className="flex items-start justify-between mb-4">
                                            <div className="flex items-center gap-3">
                                                <div className="w-10 h-10 rounded-full bg-blue-600 flex items-center justify-center text-white font-bold text-sm">
                                                    {ticket.user?.name?.charAt(0) || 'U'}
                                                </div>
                                                <div>
                                                    <p className="font-bold text-gray-900 dark:text-white">{ticket.user?.name || 'Kullanıcı'}</p>
                                                    <p className="text-xs text-gray-500 flex items-center gap-2">
                                                        Şube Yöneticisi • {new Date(ticket.created_at).toLocaleString('tr-TR')}
                                                        <span className="flex items-center gap-1 text-gray-400">
                                                            <Monitor className="w-3 h-3" />
                                                            Chrome (Windows)
                                                        </span>
                                                    </p>
                                                </div>
                                            </div>
                                        </div>
                                        <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-3">
                                            {ticket.subject}
                                        </h3>
                                        <p className="text-gray-700 dark:text-gray-300 whitespace-pre-wrap leading-relaxed">
                                            {ticket.description}
                                        </p>

                                        {ticket.attachments && ticket.attachments.length > 0 && (
                                            <div className="mt-6">
                                                <p className="text-sm font-bold text-gray-900 dark:text-white mb-3">Ekler</p>
                                                <div className="grid grid-cols-4 gap-4">
                                                    {ticket.attachments.map((url: string, idx: number) => (
                                                        <div key={idx} className="group relative rounded-xl overflow-hidden border border-gray-200 dark:border-gray-800 aspect-video bg-gray-50 dark:bg-gray-800">
                                                            <img src={url} alt={`attachment-${idx}`} className="w-full h-full object-cover" />
                                                            <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                                                                <a href={url} target="_blank" rel="noreferrer" className="p-2 bg-white rounded-full text-gray-900 hover:scale-110 transition-transform">
                                                                    <Eye className="w-4 h-4" />
                                                                </a>
                                                                <a href={url} download className="p-2 bg-white rounded-full text-gray-900 hover:scale-110 transition-transform">
                                                                    <Download className="w-4 h-4" />
                                                                </a>
                                                            </div>
                                                            <div className="absolute bottom-0 inset-x-0 p-1 bg-black/50 text-[10px] text-white truncate text-center">
                                                                Screenshot {idx + 1}
                                                            </div>
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        )}
                                    </div>

                                    {/* Reply History */}
                                    {messages.map((msg) => (
                                        <div key={msg.id} className={`bg-white dark:bg-gray-900 rounded-2xl p-6 shadow-sm border ${msg.is_internal ? 'border-yellow-200 dark:border-yellow-900/40 bg-yellow-50/30' : 'border-gray-100 dark:border-gray-800'}`}>
                                            <div className="flex items-center gap-3 mb-4">
                                                <div className={`w-10 h-10 rounded-full flex items-center justify-center text-white font-bold text-sm ${msg.sender?.unit_type === 'teknik_destek' ? 'bg-purple-600' : 'bg-blue-600'}`}>
                                                    {msg.sender?.name?.charAt(0) || 'U'}
                                                </div>
                                                <div>
                                                    <p className="font-bold text-gray-900 dark:text-white">
                                                        {msg.sender?.name || 'Bilinmeyen'}
                                                        {msg.is_internal && <span className="ml-2 text-[10px] bg-yellow-100 text-yellow-700 px-1.5 py-0.5 rounded font-bold">Dahili Not</span>}
                                                    </p>
                                                    <p className="text-xs text-gray-500">
                                                        {new Date(msg.created_at).toLocaleString('tr-TR')}
                                                    </p>
                                                </div>
                                            </div>
                                            <p className="text-gray-700 dark:text-gray-300 whitespace-pre-wrap leading-relaxed">
                                                {msg.message}
                                            </p>
                                        </div>
                                    ))}
                                </>
                            )}
                            <div ref={messagesEndRef} />
                        </div>

                        {/* Reply Form */}
                        <div className="p-6 bg-white dark:bg-gray-900 border-t border-gray-100 dark:border-gray-800">
                            <div className="relative">
                                <textarea
                                    className="w-full bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-2xl p-4 pr-12 text-sm focus:ring-2 focus:ring-purple-500 outline-none resize-none min-h-[120px]"
                                    placeholder="Yanıt yazın..."
                                    value={newMessage}
                                    onChange={(e) => setNewMessage(e.target.value)}
                                />
                                <div className="absolute right-4 top-4">
                                    <button
                                        onClick={() => setShowCannedResponses(!showCannedResponses)}
                                        className="p-2 text-gray-400 hover:text-purple-600 transition-colors"
                                    >
                                        <Plus className="w-5 h-5" />
                                    </button>

                                    {showCannedResponses && (
                                        <div className="absolute bottom-full right-0 mb-2 w-72 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl shadow-xl p-2 z-50">
                                            <p className="text-xs font-bold p-2 text-gray-500 border-b border-gray-100 dark:border-gray-700 mb-1">Hazır Cevap Seçin</p>
                                            {cannedResponses.map((res, i) => (
                                                <button
                                                    key={i}
                                                    onClick={() => {
                                                        setNewMessage(res.content);
                                                        setShowCannedResponses(false);
                                                    }}
                                                    className="w-full text-left p-2 hover:bg-gray-50 dark:hover:bg-gray-700 rounded-lg group transition-colors"
                                                >
                                                    <p className="text-xs font-bold text-gray-900 dark:text-white group-hover:text-purple-600">{res.title}</p>
                                                    <p className="text-[10px] text-gray-500 truncate">{res.content}</p>
                                                </button>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            </div>
                            <div className="mt-4 flex items-center justify-between">
                                <label className="flex items-center gap-2 cursor-pointer group">
                                    <input
                                        type="checkbox"
                                        checked={isInternal}
                                        onChange={(e) => setIsInternal(e.target.checked)}
                                        className="rounded border-gray-300 text-purple-600 focus:ring-purple-500"
                                    />
                                    <span className="text-sm text-gray-600 dark:text-gray-400 group-hover:text-gray-900 transition-colors">Dahili not</span>
                                </label>
                                <button
                                    onClick={handleSendMessage}
                                    disabled={isSubmitting || !newMessage.trim()}
                                    className="px-6 py-2 bg-gradient-to-r from-purple-600 to-purple-500 text-white rounded-xl font-bold flex items-center gap-2 hover:scale-105 transition-all shadow-lg shadow-purple-200 dark:shadow-none disabled:opacity-50 disabled:scale-100"
                                >
                                    {isSubmitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                                    Yanıtla
                                </button>
                            </div>
                        </div>
                    </div>

                    {/* Right Side: Info Panel */}
                    <div className="w-80 border-l border-gray-100 dark:border-gray-800 p-6 space-y-8 overflow-y-auto">
                        <section>
                            <h4 className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-4">Durum</h4>
                            <div className="space-y-2">
                                {['acik', 'islemde', 'cozuldu', 'kapatildi'].map((status) => (
                                    <button
                                        key={status}
                                        disabled={isUpdatingStatus}
                                        onClick={() => updateStatus(status)}
                                        className={`w-full flex items-center justify-between p-3 rounded-xl border text-sm font-medium transition-all ${ticket.status === status
                                            ? `bg-white dark:bg-gray-800 border-purple-500 text-purple-600 shadow-md translate-x-1`
                                            : 'bg-transparent border-gray-100 dark:border-gray-800 text-gray-500 hover:border-gray-300'} ${isUpdatingStatus ? 'opacity-50 cursor-not-allowed' : ''}`}
                                    >
                                        <span>{status.charAt(0).toUpperCase() + status.slice(1)}</span>
                                        {ticket.status === status && (
                                            isUpdatingStatus ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle className="w-4 h-4" />
                                        )}
                                    </button>
                                ))}
                            </div>
                        </section>

                        <section>
                            <h4 className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-4">Atama</h4>
                            <div className="relative">
                                <select
                                    className="w-full appearance-none bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl px-4 py-3 text-sm focus:ring-2 focus:ring-purple-500 outline-none pr-10"
                                    value={ticket.assigned_to || ''}
                                    onChange={(e) => updateAssignment(e.target.value || null)}
                                >
                                    <option value="">Atanmamış</option>
                                    {staffList.map(staff => (
                                        <option key={staff.id} value={staff.id}>{staff.name}</option>
                                    ))}
                                </select>
                                <ChevronDown className="w-4 h-4 absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
                            </div>
                        </section>

                        <section className="space-y-6">
                            <h4 className="text-xs font-bold text-gray-400 uppercase tracking-wider">Talep Bilgileri</h4>
                            <div className="space-y-4">
                                <div className="flex items-start gap-3">
                                    <AlertCircle className="w-4 h-4 text-gray-400 mt-1" />
                                    <div>
                                        <p className="text-xs font-bold text-gray-400">Kategori</p>
                                        <p className="text-sm text-gray-900 dark:text-white font-medium">
                                            {ticket.category === 'hata_bildirimi' ? 'Hata Bildirimi' :
                                                ticket.category === 'soru' ? 'Soru' :
                                                    ticket.category === 'ozellik_talebi' ? 'Özellik Talebi' : 'Diğer'}
                                        </p>
                                    </div>
                                </div>
                                <div className="flex items-start gap-3">
                                    <Clock className="w-4 h-4 text-gray-400 mt-1" />
                                    <div>
                                        <p className="text-xs font-bold text-gray-400">Öncelik</p>
                                        <p className="text-sm text-gray-900 dark:text-white font-medium">
                                            {ticket.priority.charAt(0).toUpperCase() + ticket.priority.slice(1)}
                                        </p>
                                    </div>
                                </div>
                                <div className="flex items-start gap-3">
                                    <User className="w-4 h-4 text-gray-400 mt-1" />
                                    <div>
                                        <p className="text-xs font-bold text-gray-400">Oluşturan</p>
                                        <p className="text-sm text-gray-900 dark:text-white font-medium">{ticket.user?.name}</p>
                                    </div>
                                </div>
                                <div className="flex items-start gap-3">
                                    <Calendar className="w-4 h-4 text-gray-400 mt-1" />
                                    <div>
                                        <p className="text-xs font-bold text-gray-400">Oluşturulma</p>
                                        <p className="text-sm text-gray-900 dark:text-white font-medium">
                                            {new Date(ticket.created_at).toLocaleDateString('tr-TR')}
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </section>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default TicketDetailModal;
